import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedLogicFlows } from '@/lib/stores/logicFlowStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useNavigate } from 'react-router-dom';
import CardBase from '../cards/CardBase';

interface LogicFlowNodeData {
  itemId: string;
  itemType: 'flow';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const LogicFlowNode = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as LogicFlowNodeData;
  
  // Get the actual logic flow data
  const flows = selectedGame?.id ? getCachedLogicFlows(selectedGame.id) : [];
  const flow = flows.find(f => f.id === nodeData.itemId);
  
  // Count nodes and edges
  const nodeCount = flow?.data?.nodes?.length || 0;
  const edgeCount = flow?.data?.edges?.length || 0;
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (flow?.id) {
      navigate(`/logic/${flow.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[200px] max-w-[300px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t-md bg-gradient-to-r from-blue-500 to-cyan-500"
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-sitemap" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Expand"
        >
          <IconProvider iconstring="fa-expand" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Body with details */}
      <div className="px-3 py-2 space-y-1">
        {nodeCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-circle-nodes" size={10} />
            <span className="truncate">{nodeCount} node(s)</span>
          </div>
        )}
        
        {edgeCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-link" size={10} />
            <span className="truncate">{edgeCount} connection(s)</span>
          </div>
        )}
        
        {flow?.description && (
          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {flow.description}
          </div>
        )}
        
        {!nodeCount && !edgeCount && !flow?.description && (
          <div className="text-xs text-muted-foreground italic">
            Empty logic flow
          </div>
        )}
      </div>
    </CardBase>
  );
});

LogicFlowNode.displayName = 'LogicFlowNode';

